import { faSearch, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import { startOfToday, endOfToday, subDays } from "date-fns";
import { isEmpty } from "lodash";
import { useState, useMemo, useEffect } from "react";
import { SelectRangeEventHandler, DateRange } from "react-day-picker";
import { twJoin } from "tailwind-merge";

import { DateRangePicker } from "src/base-components/DateRangePicker";
import {
  AccordionRoot,
  EditorAccordionItem,
} from "src/base-components/EditorAccordionItem";
import { TableComp } from "src/base-components/Table";
import { EventFilterRequestEnvironmentEnum } from "src/clients/features-retrieve";
import { EmptyState } from "src/design-system/EmptyState";
import { useSidepane } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { getTableStoreKey } from "src/eventsCatalogue/EventsCatalogue";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import {
  useEventSchema,
  useEventSchemas,
  useEventsFilters,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EventBlockProps = {
  eventType: string;
  isOpen: boolean;
  entityId: string;
  displayName: string;
};

const EventBlock: React.FC<EventBlockProps> = ({
  displayName,
  eventType,
  isOpen,
  entityId,
}) => {
  const [environment] = useEnvironment();
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const { workspace } = useWorkspaceContext();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow(
      {
        from: subDays(startOfToday(), 7),
        to: endOfToday(),
      },
      `events-time-window-${eventType}`,
    );

  const { data: schema, isLoading: isLoadingSchema } = useEventSchema(
    isOpen ? eventType : "",
  );

  const showFilterableEvents =
    isFeatureFlagEnabled(FEATURE_FLAGS.filterableEvents) &&
    featuresEventsEnabled;

  const {
    applyFilters,
    clearFilters,
    urlFilterParams,
    events,
    hasFilterCondition,
    isPending: isFilterPending,
    error: filterError,
    isRefetching: isRefetchingEvents,
    isFetchingNextPage,
    handleFetchNextPage,
  } = useEventsFilters({
    entityId,
    enabled: isOpen,
    eventType: isOpen ? eventType : "",
    endTimestamp: timeWindow?.to?.toISOString() || "",
    environment: environment as EventFilterRequestEnvironmentEnum,
    startTimestamp: timeWindow?.from?.toISOString() || "",
  });

  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { resourceId: selectedEventId, toggleSidepane } = useSidepane("event");

  const memoizedColumns = useMemo(() => {
    return schema ? getColumns(schema, environment, entitiesSchemasData) : [];
  }, [schema, entitiesSchemasData, environment]);

  const onTimeWindowChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    if (range?.from && range?.to) {
      onDateRangePickerChange(range);
    }
  };

  const headerContent = (
    <div
      className={twJoin(
        "h-13 content-center rounded-lg rounded-l-none border border-gray-200 bg-white",
        isOpen && "rounded-b-none",
      )}
    >
      <div
        className={`flex items-center ${showFilterableEvents ? "" : "gap-x-2 pr-4"}`}
      >
        <DateRangePicker
          disabled={isFilterDropdownOpen}
          suffixIcon={!isFilterDropdownOpen}
          usePredefinedDates={!isFilterDropdownOpen}
          value={dateRangePickerValue}
          borderless
          onChange={onTimeWindowChange}
        />
        {showFilterableEvents && (
          <EventsFilterDropdown
            dateRangeValue={timeWindow}
            eventType={eventType}
            hasFilterCondition={hasFilterCondition}
            isOpen={isFilterDropdownOpen}
            urlFilterParams={urlFilterParams}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            onDateRangeChange={onTimeWindowChange}
            onOpenChange={setIsFilterDropdownOpen}
          />
        )}
      </div>
    </div>
  );

  const isLoading = isFilterPending || isLoadingSchema || isRefetchingEvents;

  const renderEventsContent = () => {
    // Show filter error if present (including 400 errors from POST request)
    const filterErrorMessage = errorMessage(filterError?.data);
    if ((!isLoading && isEmpty(events)) || filterError) {
      return (
        <EmptyState
          actionButton={
            hasFilterCondition
              ? {
                  text: "Clear filters",
                  onClick: clearFilters,
                  icon: faTrashAlt,
                }
              : undefined
          }
          dataLoc={
            filterError
              ? `${eventType}-filter-error-state`
              : `${eventType}-empty-state`
          }
          description={
            filterErrorMessage ||
            (hasFilterCondition
              ? "No data found for the selected filters. Adjust the filters, and try running the query again."
              : "Try adjusting the filter to find what you are looking for")
          }
          headline={filterError ? "Error loading events" : "No events found"}
          icon={faSearch}
          variant={filterError ? "error" : "default"}
        />
      );
    }

    return (
      <div className="relative flex h-[364px] w-full overflow-x-auto">
        <TableComp
          columns={memoizedColumns}
          data={events}
          dataLoc={`${eventType}-events-table`}
          frameClassName="w-full"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          rowPropsGetter={(row) => ({
            onClick: () => {
              toggleSidepane({
                id: row.original.payload.id,
                eventType: eventType,
              });
            },
            classNameOverrides: twJoin(
              row.original.payload.id === selectedEventId
                ? "selected bg-gray-50"
                : "hover:bg-gray-50",
              "cursor-pointer",
            ),
          })}
          uiStoreKey={getTableStoreKey(eventType)}
          variant="compact"
          onScrolledToTheBottom={handleFetchNextPage}
        />
      </div>
    );
  };

  return (
    <EditorAccordionItem
      className="decideScrollbar overflow-hidden p-4 pt-3"
      contentWrapperClassName="bg-white border-b border-x border-gray-200 rounded-b-lg overflow-hidden"
      dataLoc={`${eventType}-group`}
      headerClassName={twJoin(
        "rounded-lg rounded-r-none border border-r-0 border-gray-200 bg-white transition-all",
        isOpen && "rounded-b-none",
      )}
      headerContent={headerContent}
      title={displayName}
      value={eventType}
    >
      {renderEventsContent()}
    </EditorAccordionItem>
  );
};

type EntityEventsProps = {
  entityId: string;
};

export const EntityEvents: React.FC<EntityEventsProps> = ({ entityId }) => {
  const { data: eventTypes, isLoading: isLoadingEventTypes } =
    useEventSchemas();
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  useEffect(() => {
    if (eventTypes && eventTypes.length > 0) {
      setAccordionValue([eventTypes[0].event_type]);
    }
  }, [eventTypes]);

  if (!eventTypes?.length && !isLoadingEventTypes) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          dataLoc="events-empty-state"
          description="There are no events found related to this entity yet."
          headline="No events found"
          icon={faSearch}
        />
      </WhitePane>
    );
  }

  return (
    <AccordionRoot
      className="flex flex-col gap-y-3"
      type="multiple"
      value={accordionValue}
      onValueChange={setAccordionValue}
    >
      {eventTypes?.map((eventType) => (
        <EventBlock
          key={eventType.event_type}
          displayName={eventType.display_name_plural}
          entityId={entityId}
          eventType={eventType.event_type}
          isOpen={accordionValue.includes(eventType.event_type)}
        />
      ))}
    </AccordionRoot>
  );
};

export const EventsTabContent = () => {
  const { id } = useParamsDecode<EntityViewParams>();

  return (
    <div className="p-5">
      <EntityEvents entityId={id} />
    </div>
  );
};
